import type { Map as MapboxMap, StyleSpecification } from 'mapbox-gl';
import { attachSiteModel, type HqMapMode } from './apsHqModel';
import type { MapLightPreset } from './customModelLighting';

// Source construction anchor. The Standard presentation fit below deliberately
// fills the stylized block rather than retaining surveyed-scale dimensions.
export const COOK_ORIGIN: [number, number] = [-120.99607273936272, 37.63623028258303];
const EAST_X = 0.6866580442939417;
const SOUTH_X = 0.7266381342091639;
// Mapbox converts glTF Y-up to East/South/Up itself, before this rotation.
// Retain the tiny horizontal scale correction embedded in the supplied matrix.
export const COOK_HORIZONTAL_SCALE = Math.hypot(EAST_X, SOUTH_X);
export const COOK_ROTATION = Math.atan2(SOUTH_X, EAST_X) * 180 / Math.PI;
export const COOK_FIT_SCALE: [number, number, number] = [142 / 126, 111 / 95.3, 1];
// Keep the lot centered between F/E and shift slightly toward Ninth Street.
const FIT_OFFSET = [61 - 61 * COOK_FIT_SCALE[0], -5.2 + 3.35 * COOK_FIT_SCALE[1]];
function sourceLocalToLngLat(x: number, y: number): [number, number] {
  const east = EAST_X * x + SOUTH_X * y;
  const north = -SOUTH_X * x + EAST_X * y;
  const metresPerDegree = Math.PI * 6371008.8 / 180;
  return [COOK_ORIGIN[0] + east / (metresPerDegree * Math.cos(COOK_ORIGIN[1] * Math.PI / 180)),
    COOK_ORIGIN[1] + north / metresPerDegree];
}
export const COOK_FIT_ORIGIN = sourceLocalToLngLat(FIT_OFFSET[0], FIT_OFFSET[1]);
export function cookLocalToLngLat(x: number, y: number): [number, number] {
  return sourceLocalToLngLat(x * COOK_FIT_SCALE[0] + FIT_OFFSET[0], y * COOK_FIT_SCALE[1] + FIT_OFFSET[1]);
}
// Presentation street-edge envelope from the approved grounds, including the
// clipped Ninth/E corner. This suppresses only basemap objects, never the asset.
export const COOK_BOUNDARY = [[-2, -51], [122.8, -51], [124, -49.8],
  [124, 44.3], [-2, 44.3], [-2, -51]].map(([x, y]) => cookLocalToLngLat(x, y));
export function cookModelStyle(uri: string): StyleSpecification {
  return {
    version: 8,
    sources: {
      'cook-marketplace-asset': { type: 'model', models: {
        'cook-marketplace': { uri, position: COOK_FIT_ORIGIN },
      } },
      'cook-marketplace-boundary': { type: 'geojson', data: {
        type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [COOK_BOUNDARY] },
      } },
    },
    layers: [
      { id: 'cook-marketplace-replacement', type: 'clip', source: 'cook-marketplace-boundary',
        layout: { 'clip-layer-types': ['model', 'symbol'], 'clip-layer-scope': ['basemap'] } },
      { id: 'cook-marketplace-model', type: 'model', source: 'cook-marketplace-asset', slot: 'middle',
        paint: { 'model-rotation': [0, 0, COOK_ROTATION], 'model-scale': [COOK_HORIZONTAL_SCALE * COOK_FIT_SCALE[0], COOK_HORIZONTAL_SCALE * COOK_FIT_SCALE[1], 1],
          'model-translation': [0, 0, 0.03], 'model-color-mix-intensity': 0, 'model-opacity': 1,
          'model-cast-shadows': true, 'model-receive-shadows': true, 'model-emissive-strength': 0,
          'model-cutoff-fade-range': 0, 'model-line-cutout-mode': 'disabled' },
      },
    ],
  };
}
export function attachCookMarketplaceModel(map: MapboxMap, mode: HqMapMode,
  onError: (message: string | null) => void, lightPreset: MapLightPreset) {
  const uri = new URL(`${import.meta.env.BASE_URL}models/cook-marketplace/batched.glb?v=fc3eb2ab3cf3`, window.location.href).href;
  return attachSiteModel(map, mode, onError, lightPreset, {
    name: 'Cook Marketplace', asset: 'cook-marketplace-asset', model: 'cook-marketplace-model',
    clip: 'cook-marketplace-replacement', style: cookModelStyle(uri), errorPattern: /cook-marketplace/i,
  });
}
